import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { fetchUserDataAction, updateUserDataAction } from "../../store/slices/authSlice";
import StatCards from "../../components/common/MyProfile/StatCards";
import ProfileView from "../../components/common/MyProfile/ProfileView";
import ProfileEdit from "../../components/common/MyProfile/ProfileEdit";
import PostCard from "../../components/common/RecentPostCard/PostCard";
import { useTheme, useMediaQuery } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { isTokenExpired } from "../../utils/authUtils";
import { getCampaigns } from "../../api/services/campaignService";
import { getCampaignUrl } from "../../utils/slugUtils";
import ProfileCampaignSlider from "../../components/common/MyProfile/ProfileCampaignSlider";
import { matchesCategory } from "../../utils/helper";

const MyProfile = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isBelow1200 = useMediaQuery("(max-width:1199px)");
  const isBelow1023 = useMediaQuery("(max-width:1023px)");
  
  // Get user data from Redux store
  const { user, loading } = useSelector((state) => state.auth);

  const handleBackToMenu = () => {
    navigate('/profile');
  };

  // Fetch user data if not available, only when a valid token exists
  useEffect(() => {
    if (!user && !loading) {
      const token = localStorage.getItem("authToken");
      if (!token || isTokenExpired(token)) {
        if (token) localStorage.removeItem("authToken");
        return;
      }
      dispatch(fetchUserDataAction());
    }
  }, [user, loading, dispatch]);

  // Update local data when Redux user data changes
  useEffect(() => {
    if (user) {
      const updatedUserData = {
        id: user.id || "N/A",
        name: user.name || "User",
        role: "Donor",
        email: user.email || "",
        phone: user.phone || "",
        dob: user.dob || "",
        passport: user.passport || "",
        pan: user.pan || "",
        gender: user.gender || "",
        profilePicture: user.profilePicture || null,
      };
      setLocalUserData(updatedUserData);
    }
  }, [user]);

  const [isEditing, setIsEditing] = useState(false);
  const [urgentCampaigns, setUrgentCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  
  // Use Redux store data with fallbacks
  const userData = {
    id: user?.id || "N/A",
    name: user?.name || "User",
    role: "Donor",
    email: user?.email || "",
    phone: user?.phone || "",
    dob: user?.dob || "",
    passport: user?.passport || "",
    pan: user?.pan || "",
    gender: user?.gender || "",
    profilePicture: user?.profilePicture || null,
  };
  
  const [localUserData, setLocalUserData] = useState(userData);

  // Fetch urgent campaigns
  useEffect(() => {
    let isMounted = true;
    const fetchUrgentCampaigns = async () => {
      setCampaignsLoading(true);
      try {
        const response = await getCampaigns();
        if (isMounted) {
          const allCampaigns = response?.data || [];
          // Filter for Urgent category
          const urgent = allCampaigns.filter(
            (campaign) => matchesCategory(campaign.category, "Urgent")
          );
          // Sort by latest (createdAt or updatedAt) - most recent first
          const sorted = urgent.sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.createdAt || 0);
            const dateB = new Date(b.updatedAt || b.createdAt || 0);
            return dateB - dateA; // Descending order (newest first)
          });
          // Get latest 3
          setUrgentCampaigns(sorted.slice(0, 3));
        }
      } catch (error) {
        console.error("Error fetching urgent campaigns:", error);
        if (isMounted) {
          setUrgentCampaigns([]);
        }
      } finally {
        if (isMounted) {
          setCampaignsLoading(false);
        }
      }
    };

    fetchUrgentCampaigns();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async (updatedData) => {
    // Map to API payload keys
    const payload = {
      id: updatedData.id,
      name: updatedData.name,
      email: updatedData.email,
      phone: updatedData.phone,
      dob: updatedData.dob,
      gender: updatedData.gender,
      passport: updatedData.passport || "",
      pan: updatedData.pan || "",
      profilePicture: updatedData.profilePicture,
    };

    try {
      const resultAction = await dispatch(updateUserDataAction(payload));
      if (updateUserDataAction.fulfilled.match(resultAction)) {
        // Re-fetch profile to ensure we have authoritative values (e.g., processed image URL)
        const refetchAction = await dispatch(fetchUserDataAction());
        if (fetchUserDataAction.fulfilled.match(refetchAction)) {
          const saved = refetchAction.payload;
          setLocalUserData({
            id: saved.id || "N/A",
            name: saved.name || "User",
            role: "Donor",
            email: saved.email || "",
            phone: saved.phone || "",
            dob: saved.dob || "",
            passport: saved.passport || "",
            pan: saved.pan || "",
            gender: saved.gender || "",
            profilePicture: saved.profilePicture || null,
          });
          setIsEditing(false);
        } else {
          setIsEditing(false);
        }
      } else {
        console.error("Failed to update:", resultAction.payload);
      }
    } catch (err) {
      console.error("Error updating profile:", err);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  // If mobile, the StatCards are already in the sidebar, so don't show them here
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      {/* Mobile header with back button */}
      {isMobile && (
        <Box 
        onClick={handleBackToMenu}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          marginBottom: "20px",
          width: "100%",
        }}>
          <Box 
            
            sx={{ 
              cursor: "pointer",
              display: "flex",
              alignItems: "center"
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="#1D1D1B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
          <Typography sx={{
            fontWeight: 600,
            fontSize: "14px",
            lineHeight: "100%",
            letterSpacing: "0%",
            color: "#1D1D1B"
          }}>
            My Profile
          </Typography>
        </Box>
      )}
      {/* Statistics Cards - show on desktop */}
      {!isMobile && (
        <Box sx={{ mb: "30px", width: "100%" }}>
          <StatCards user={user} />
        </Box>
      )}

      {/* Profile Section */}
      <Box
        sx={{
          mb: isBelow1023 ? "20px" : "40px",
          display: "flex",
          justifyContent: "start",
          gap: "20px",
          width: "100%",
          flexDirection: isBelow1023 ? "column" : "row",
        }}
      >
        <Box sx={{ width: "100%" }}>
          {isEditing ? (
            <Box>
              <ProfileEdit
                userData={localUserData}
                onSave={handleSave}
                onCancel={handleCancel}
              />
            </Box>
          ) : (
            <Box>
              <ProfileView userData={localUserData} onEdit={handleEdit} showCampaignSlider={false} />
            </Box>
          )}
        </Box>
        
        {!isEditing && !isMobile && (
          <Box
            sx={{
              borderRadius: "12px",
              border: "1px solid #DBE0E5",
              p: "20px",
              minWidth: isBelow1200 ? "370px" : "427px",
              display: isBelow1023 ? "none" : "block",
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: "700",
                fontSize: "18px",
                color: "#1D1D1B",
                marginBottom: "20px",
              }}
            >
              Urgent Campaigns
            </Typography>
            <Box>
              {campaignsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : urgentCampaigns.length === 0 ? (
                <Typography
                  sx={{
                    color: "#686866",
                    fontSize: "14px",
                    textAlign: "center",
                    py: 2,
                  }}
                >
                  No urgent campaigns available
                </Typography>
              ) : (
                urgentCampaigns.map((campaign) => (
                  <PostCard
                    key={campaign.id}
                    image={campaign.image || campaign.thumbnail_image || ""}
                    title={campaign.title || ""}
                    date={campaign.createdAt || campaign.updatedAt || new Date().toISOString()}
                    onClick={() => {
                      const url = getCampaignUrl(campaign);
                      if (url) {
                        navigate(url);
                      } else {
                        navigate('/campaigns');
                      }
                    }}
                  />
                ))
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Statistics Cards - show on mobile after profile section, before urgent campaign */}
      {isMobile && !isEditing && (
        <Box sx={{ width: "100%" }}>
          <StatCards user={user} />
        </Box>
      )}

      {/* Urgent campaign cards: only on /profile/my-profile, not on index /profile */}
      {isMobile &&
        !isEditing &&
        location.pathname === "/profile/my-profile" && (
        <Box sx={{ width: "100%" }}>
          <ProfileCampaignSlider />
        </Box>
      )}
    </Box>
  );
};

export default MyProfile;
